import {
  DatasetMetadata,
  MULTIPART_CHUNK_SIZE,
  MULTIPART_THRESHOLD_BYTES,
  UploadFile,
  abortMultipartUpload,
  completeMultipartUpload,
  initiateMultipartUpload,
  uploadDataset,
  uploadPartToS3,
} from '@app/services/dataUploadService'
import { useState } from 'react'

export const useDataUpload = () => {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const runMultipartUpload = async (
    primary: File,
    companions: UploadFile[],
    uploadId: string,
    key: string,
    presignedUrls: string[],
    metadata: DatasetMetadata,
  ) => {
    let completed = 0

    try {
      // Parts track 0–90%; the remaining 10% is reserved for the complete call
      const parts = await Promise.all(
        presignedUrls.map(async (url, i) => {
          const chunk = primary.slice(i * MULTIPART_CHUNK_SIZE, (i + 1) * MULTIPART_CHUNK_SIZE)
          const etag = await uploadPartToS3(url, chunk)
          setProgress(Math.round((++completed / presignedUrls.length) * 90))
          return { PartNumber: i + 1, ETag: etag }
        }),
      )
      await completeMultipartUpload(key, uploadId, parts, metadata, companions)
    } catch (err) {
      // Abort tells S3 to discard the in-progress upload and free its storage
      await abortMultipartUpload(key, uploadId)
      throw err
    }
  }

  const upload = async (files: File[], metadata: DatasetMetadata): Promise<void> => {
    const [primary, ...companionFiles] = files
    const isMultipart = primary.size >= MULTIPART_THRESHOLD_BYTES

    setUploading(true)
    setProgress(0)
    setStatus('idle')
    setErrorMessage(null)

    try {
      if (isMultipart) {
        const total_parts = Math.ceil(primary.size / MULTIPART_CHUNK_SIZE)
        const { uploadId, key, presignedUrls } = await initiateMultipartUpload(
          primary,
          metadata.name,
          total_parts,
        )
        if (!presignedUrls?.length) throw new Error('Initiate response missing presigned URLs')
        const companions = await Promise.all(
          companionFiles.map(async (f) => ({
            filename: f.name,
            contentType: f.type || undefined,
            data: await new Promise<string>((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve((reader.result as string).split(',')[1])
              reader.onerror = reject
              reader.readAsDataURL(f)
            }),
          })),
        )
        await runMultipartUpload(primary, companions, uploadId, key, presignedUrls, metadata)
      } else {
        await uploadDataset(files, metadata)
      }
      setProgress(100)
      setStatus('success')
    } catch (err) {
      setStatus('error')
      // @TODO: as we solidify the API errors, we can catch them here and present them cleanly instead of a generic message
      setErrorMessage('Upload failed, please try again.')
      throw err
    } finally {
      setUploading(false)
    }
  }

  return { upload, uploading, progress, status, errorMessage }
}
